import { Component, Output, EventEmitter, OnInit, HostListener, ElementRef, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { ThemeService } from '../../services/theme.service';
import { AuthService } from '../../services/auth.service';
import { VersionCheckService } from '../../services/version-check.service';
import { AuthUser } from '../../data-models/auth-user.model';

/**
 * Top Bar Navigation Component - Admin Portal
 * Contains theme toggle and user profile dropdown
 * Based on web portal design but for internal staff use
 */
@Component({
  selector: 'app-topbar',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './topbar.component.html',
  styleUrls: ['./topbar.component.scss']
})
export class TopbarComponent implements OnInit {
  @Output() toggleSidebar = new EventEmitter<void>();

  private versionCheckService = inject(VersionCheckService);
  readonly updateAvailable = this.versionCheckService.updateAvailable;

  currentTheme$;
  showUserMenu: boolean = false;
  currentUser: AuthUser | null = null;

  constructor(
    private themeService: ThemeService,
    private authService: AuthService,
    private router: Router,
    private elementRef: ElementRef
  ) {
    this.currentTheme$ = this.themeService.theme$;
  }

  ngOnInit(): void {
    this.authService.currentUser$.subscribe(user => {
      this.currentUser = user;
    });
  }

  /**
   * Close dropdown when clicking outside
   */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const clickedInside = this.elementRef.nativeElement.contains(event.target);
    if (!clickedInside && this.showUserMenu) {
      this.showUserMenu = false;
    }
  }

  /**
   * Toggle theme between light and dark modes
   */
  toggleTheme(): void {
    this.themeService.toggleTheme();
  }

  /**
   * Toggle user dropdown menu
   */
  toggleUserMenu(): void {
    this.showUserMenu = !this.showUserMenu;
  }

  /**
   * Emit sidebar toggle event
   */
  onToggleSidebar(): void {
    this.toggleSidebar.emit();
  }

  /**
   * Get user display name
   */
  getUserName(): string {
    const displayName = this.currentUser?.displayName || this.currentUser?.email?.split('@')[0] || 'Admin User';
    const names = displayName.split(' ');

    // Capitalize each name part
    const capitalizedNames = names.map(name =>
      name.charAt(0).toUpperCase() + name.slice(1).toLowerCase()
    );

    return capitalizedNames.join(' ');
  }

  /**
   * Navigate to profile
   */
  goToProfile(): void {
    this.router.navigate(['/account/profile']);
    this.showUserMenu = false;
  }

  /**
   * Navigate to settings
   */
  goToSettings(): void {
    this.router.navigate(['/settings']);
    this.showUserMenu = false;
  }

  /**
   * Logout user
   */
  async logout(): Promise<void> {
    this.showUserMenu = false;
    await this.authService.logout();
  }

  reloadForUpdate(): void {
    this.versionCheckService.reloadApp();
  }
}
